import { createPublicClient, http, type ContractFunctionParameters, type Hex } from 'viem';
import { mainnet } from 'viem/chains';
import type { OverviewPool, PoolDef } from '../../shared/types';
import { erc20Abi, erc4626Abi } from '../../shared/abi/erc20';
import { cometAbi } from '../../shared/abi/compound-v3';
import { MORPHO_BLUE, morphoBlueAbi } from '../../shared/abi/morpho';
import { maplePoolAbi } from '../../shared/abi/maple';
import { stabilityPoolAbi } from '../../shared/abi/liquity-v2';
import { MULTICALL3_ADDRESS, multicall3Abi } from '../../shared/abi/multicall3';
import { fromUnits, mean } from '../../shared/format';
import type { WalletEntry } from './vault';

/**
 * Browser-only position reads against the public RPC. Addresses never touch
 * the Worker; this module is imported by the dashboard only after the vault
 * is unlocked.
 */
export type PositionRow = { poolId: string; label: string; address: string; assets: number; collGain?: number };
export type GasRow = { label: string; address: string; eth: number };
export type PositionsResult = { positions: PositionRow[]; gas: GasRow[] };

type Tag =
  | { t: 'eth'; entry: WalletEntry }
  | { t: 'market'; poolId: string }
  | { t: 'assets6'; poolId: string; entry: WalletEntry }
  | { t: 'shares'; poolId: string; entry: WalletEntry; vault: Hex }
  | { t: 'mmpos'; poolId: string; entry: WalletEntry }
  | { t: 'bold'; poolId: string; entry: WalletEntry; part: 'deposit' | 'yield' | 'coll' };

export async function readPositions(rpcUrl: string, pools: PoolDef[], entries: WalletEntry[]): Promise<PositionsResult> {
  const client = createPublicClient({ chain: mainnet, transport: http(rpcUrl) });
  const calls: ContractFunctionParameters[] = [];
  const tags: Tag[] = [];
  const push = (call: ContractFunctionParameters, tag: Tag) => { calls.push(call); tags.push(tag); };

  for (const e of entries) push({ address: MULTICALL3_ADDRESS, abi: multicall3Abi, functionName: 'getEthBalance', args: [e.address as Hex] }, { t: 'eth', entry: e });
  for (const p of pools) {
    const a = p.addresses;
    if (p.kind === 'morpho-market' && a.marketId) push({ address: MORPHO_BLUE, abi: morphoBlueAbi, functionName: 'market', args: [a.marketId] }, { t: 'market', poolId: p.id });
    for (const e of entries) {
      const who = e.address as Hex;
      switch (p.kind) {
        case 'aave-v3':
          if (a.aToken) push({ address: a.aToken, abi: erc20Abi, functionName: 'balanceOf', args: [who] }, { t: 'assets6', poolId: p.id, entry: e });
          break;
        case 'compound-v3':
          if (a.comet) push({ address: a.comet, abi: cometAbi, functionName: 'balanceOf', args: [who] }, { t: 'assets6', poolId: p.id, entry: e });
          break;
        case 'morpho-vault':
          if (a.vault) push({ address: a.vault, abi: erc4626Abi, functionName: 'balanceOf', args: [who] }, { t: 'shares', poolId: p.id, entry: e, vault: a.vault });
          break;
        case 'maple-syrup':
          if (a.pool) push({ address: a.pool, abi: maplePoolAbi, functionName: 'balanceOf', args: [who] }, { t: 'shares', poolId: p.id, entry: e, vault: a.pool });
          break;
        case 'morpho-market':
          if (a.marketId) push({ address: MORPHO_BLUE, abi: morphoBlueAbi, functionName: 'position', args: [a.marketId, who] }, { t: 'mmpos', poolId: p.id, entry: e });
          break;
        case 'liquity-sp':
          if (a.stabilityPool) {
            const sp = { address: a.stabilityPool, abi: stabilityPoolAbi } as const;
            push({ ...sp, functionName: 'getCompoundedBoldDeposit', args: [who] }, { t: 'bold', poolId: p.id, entry: e, part: 'deposit' });
            push({ ...sp, functionName: 'getDepositorYieldGain', args: [who] }, { t: 'bold', poolId: p.id, entry: e, part: 'yield' });
            push({ ...sp, functionName: 'getDepositorCollGain', args: [who] }, { t: 'bold', poolId: p.id, entry: e, part: 'coll' });
          }
          break;
        case 'eth-gas':
          break;
      }
    }
  }
  if (calls.length === 0) return { positions: [], gas: [] };
  const r1 = (await client.multicall({ contracts: calls, allowFailure: true, batchSize: 0 })) as { status: string; result?: unknown }[];
  const val = (i: number): unknown => (r1[i]?.status === 'success' ? r1[i]!.result : null);

  // Second round: share → asset conversions for ERC-4626 style vaults.
  const conv: ContractFunctionParameters[] = [];
  const convIdx: number[] = [];
  tags.forEach((tag, i) => {
    const v = val(i);
    if (tag.t === 'shares' && typeof v === 'bigint' && v > 0n) {
      conv.push({ address: tag.vault, abi: erc4626Abi, functionName: 'convertToAssets', args: [v] });
      convIdx.push(i);
    }
  });
  const r2 = conv.length ? ((await client.multicall({ contracts: conv, allowFailure: true, batchSize: 0 })) as { status: string; result?: unknown }[]) : [];
  const converted = new Map<number, bigint>();
  convIdx.forEach((i, j) => { const r = r2[j]; if (r?.status === 'success' && typeof r.result === 'bigint') converted.set(i, r.result); });

  const markets = new Map<string, readonly bigint[]>();
  tags.forEach((tag, i) => { const v = val(i); if (tag.t === 'market' && Array.isArray(v)) markets.set(tag.poolId, v as readonly bigint[]); });

  const gas: GasRow[] = [];
  const rows = new Map<string, PositionRow>();
  const row = (poolId: string, e: WalletEntry) => {
    const k = `${poolId}|${e.address}`;
    let r = rows.get(k);
    if (!r) { r = { poolId, label: e.label, address: e.address, assets: 0 }; rows.set(k, r); }
    return r;
  };
  tags.forEach((tag, i) => {
    const v = val(i);
    switch (tag.t) {
      case 'eth':
        gas.push({ label: tag.entry.label, address: tag.entry.address, eth: typeof v === 'bigint' ? fromUnits(v, 18) : 0 });
        break;
      case 'assets6':
        if (typeof v === 'bigint') row(tag.poolId, tag.entry).assets += fromUnits(v, 6);
        break;
      case 'shares': {
        const assets = converted.get(i);
        if (assets !== undefined) row(tag.poolId, tag.entry).assets += fromUnits(assets, 6);
        break;
      }
      case 'mmpos': {
        const mk = markets.get(tag.poolId);
        if (Array.isArray(v) && mk && mk[1] && mk[1] > 0n) {
          const shares = (v as readonly bigint[])[0]!;
          row(tag.poolId, tag.entry).assets += fromUnits((shares * mk[0]!) / mk[1], 6);
        }
        break;
      }
      case 'bold':
        if (typeof v === 'bigint') {
          const r = row(tag.poolId, tag.entry);
          if (tag.part === 'coll') r.collGain = (r.collGain ?? 0) + fromUnits(v, 18);
          else r.assets += fromUnits(v, 18);
        }
        break;
      case 'market':
        break;
    }
  });
  return { positions: [...rows.values()].filter((r) => r.assets > 0.000001 || (r.collGain ?? 0) > 0), gas };
}

export const EXIT_WARN = 0.1;
export const EXIT_CRITICAL = 0.25;
/** Roughly two withdrawals at 100 gwei. */
export const GAS_RESERVE_ETH = 0.05;

export type Level = 'ok' | 'warn' | 'critical';
export type PoolPosition = {
  poolId: string;
  total: number;
  /** position ÷ pool total supplied */
  exposure: number | null;
  /** position ÷ pool available liquidity: how much of the exit door you would need; Infinity when the pool is drained */
  exitCoverage: number | null;
  level: Level;
  /** pool supply APY, mean of the 7-day overview series (hourly Aave rates swing too much); latest snapshot as fallback */
  apy: number | null;
  /** position in USD: BOLD at its price, USDC at par */
  usd: number;
  rows: PositionRow[];
};

export type BlendedYield = {
  /** USD-weighted APY over positions whose pool reports one */
  apy: number | null;
  annual: number;
  monthly: number;
  /** USD left out because the pool has no APY */
  uncovered: number;
};

/** Pure: weight each pool's APY by what you actually hold there. */
export function blendedYield(summary: PoolPosition[]): BlendedYield {
  const priced = summary.filter((s) => s.apy !== null);
  const weight = priced.reduce((n, s) => n + s.usd, 0);
  const annual = priced.reduce((n, s) => n + s.usd * s.apy!, 0);
  const uncovered = summary.reduce((n, s) => n + s.usd, 0) - weight;
  return { apy: weight > 0 ? annual / weight : null, annual, monthly: annual / 12, uncovered };
}

/** Pure: fold rows per pool and grade exit coverage against the pool's live snapshot. */
export function summarize(positions: PositionRow[], pools: OverviewPool[]): PoolPosition[] {
  const out: PoolPosition[] = [];
  for (const p of pools) {
    const rows = positions.filter((r) => r.poolId === p.pool.id);
    if (rows.length === 0) continue;
    const total = rows.reduce((s, r) => s + r.assets, 0);
    const supplied = p.latest?.totalSupplied ?? null;
    const avail = p.latest?.availableLiquidity ?? null;
    const exposure = supplied ? total / supplied : null;
    // A drained pool (Aave sat at 0 USDC for hours on 2026-10-30) is the worst case, not an unknown.
    const exitCoverage = avail === null ? null : avail > 0 ? total / avail : total > 0 ? Infinity : null;
    const level: Level = exitCoverage === null ? 'ok' : exitCoverage > EXIT_CRITICAL ? 'critical' : exitCoverage > EXIT_WARN ? 'warn' : 'ok';
    const usd = total * (p.pool.asset === 'BOLD' ? p.latest?.price ?? 1 : 1);
    const apy = mean(p.series.map((x) => x.supplyApy)) ?? p.latest?.supplyApy ?? null;
    out.push({ poolId: p.pool.id, total, exposure, exitCoverage, level, apy, usd, rows });
  }
  return out;
}

export const gasLevel = (eth: number): Level => (eth < GAS_RESERVE_ETH ? 'warn' : 'ok');
